/**
 * useCategories Hook
 * 
 * Fetches categories by type (expense/income) from the API.
 * Uses cache so CategorySettings and ManageDetailsPage show data instantly when preloaded.
 */

import { useEffect, useState, useRef } from "react";
import type React from "react";
import { api } from "../../../services/api";
import { TransactionCategory } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";

const categoriesCache = new Map<string, { data: TransactionCategory[]; timestamp: number }>();
const CACHE_TTL_MS = 90 * 1000; // 90 seconds - enough for Settings -> Categories -> Manage Details flow

export interface UseCategoriesParams {
  workspaceId: string;
  type: "expense" | "income";
}

export interface UseCategoriesResult {
  categories: TransactionCategory[];
  loading: boolean;
  error: string;
  setCategories: React.Dispatch<React.SetStateAction<TransactionCategory[]>>;
  refresh: () => Promise<void>;
}

/**
 * Hook for fetching categories by type
 */
export const useCategories = ({ workspaceId, type }: UseCategoriesParams): UseCategoriesResult => {
  const cacheKey = `${workspaceId}:${type}`;
  const cached = workspaceId ? categoriesCache.get(cacheKey) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_TTL_MS);

  const [categories, setCategories] = useState<TransactionCategory[]>(hasValidCache && cached ? cached.data : []);
  const [loading, setLoading] = useState<boolean>(!hasValidCache);
  const [error, setError] = useState<string>("");
  const isInitialMount = useRef<boolean>(!hasValidCache);

  const fetchCategories = async (): Promise<void> => {
    if (!workspaceId || isDemoMode()) {
      setLoading(false);
      setCategories([]);
      isInitialMount.current = false;
      return;
    }

    // Only show full loading spinner on initial mount, not when switching tabs
    if (isInitialMount.current) {
      setLoading(true);
    } else {
      // Clear categories immediately when switching tabs to prevent showing wrong categories
      setCategories([]);
    }
    setError("");

    try {
      const data = await api.getCategories(workspaceId, type);
      if (workspaceId) {
        categoriesCache.set(cacheKey, { data, timestamp: Date.now() });
      }
      setCategories(data);
      setLoading(false);
      isInitialMount.current = false;
    } catch (err: any) {
      console.error("Error fetching categories:", err);
      setError(err.message || "Failed to load categories");
      setLoading(false);
      isInitialMount.current = false;
    }
  };

  useEffect(() => {
    const key = `${workspaceId}:${type}`;
    const entry = workspaceId ? categoriesCache.get(key) : null;
    const valid = !!(entry && (Date.now() - entry.timestamp) < CACHE_TTL_MS);
    if (valid) return; // Skip fetch when cache is valid so we don't overwrite or race
    fetchCategories();
  }, [workspaceId, type]);

  // When we're loading, watch the cache so we show data as soon as preloader (or another instance) fills it
  useEffect(() => {
    if (!loading || !workspaceId) return;
    const key = `${workspaceId}:${type}`;
    const interval = setInterval(() => {
      const entry = categoriesCache.get(key);
      const valid = entry && (Date.now() - entry.timestamp) < CACHE_TTL_MS;
      if (valid) {
        setCategories(entry.data);
        setLoading(false);
        setError("");
      }
    }, 100);
    return () => clearInterval(interval);
  }, [workspaceId, type, loading]);

  // Keep cache in sync when categories change (e.g. after save); never write empty so we don't overwrite good cache
  useEffect(() => {
    if (workspaceId && categories.length > 0) {
      categoriesCache.set(cacheKey, { data: categories, timestamp: Date.now() });
    }
  }, [workspaceId, cacheKey, categories]);

  return {
    categories,
    loading,
    error,
    setCategories,
    refresh: fetchCategories,
  };
};
